import isUndefined from 'lodash/isUndefined';
import { checkCondition, calculateResult } from '../ConditionProcessor/ConditionProcessor';
import { ConfigurableNodeError, getTicketFieldBasedOnPartner } from '../../../../../../util';

export const processConfigurableNode = (
  configurableNode,
  ticketFields,
  selectedOrder,
  selectedSubscription,
  isSpecialist
) => {
  if (!configurableNode) {
    throw new ConfigurableNodeError('Unable to process node due to an error in the configurable logic');
  }

  for (const node of configurableNode) {
    const { fieldName, fieldOperator, fieldValue, fieldUnit, partner } = node;

    /**
     * Based on the partner property, we grab the ticket value from different sources.
     * For now, "common" will grab from the Zendesk (or other) ticket integrations.
     * "rbi" will grab from the selectedOrder as these are custom backend values that
     * have no association with the ticket fields.
     */
    const fieldValueFromTicket = getTicketFieldBasedOnPartner({
      partner,
      fieldName,
      ticketFields,
      selectedOrder,
      selectedSubscription,
    });
    if (isUndefined(fieldValueFromTicket)) {
      let missingFieldValueFromTicketError = 'Unable to process node due to the ticket missing the field';
      if (isSpecialist) {
        missingFieldValueFromTicketError = `${missingFieldValueFromTicketError}\n[Missing Field: ${fieldName}]`;
      }
      throw new ConfigurableNodeError(missingFieldValueFromTicketError);
    }

    try {
      if (
        !checkCondition({
          fieldValue: fieldValueFromTicket,
          checkType: fieldOperator,
          targetValue: fieldValue,
          targetUnit: fieldUnit,
        })
      ) {
        return false;
      }
    } catch (error) {
      const errorCause = { fieldName, fieldOperator, fieldValue, fieldUnit, fieldValueFromTicket, partner };
      throw new ConfigurableNodeError(error.message, errorCause);
    }
  }

  return true;
};

export const processConfigurableCalculationNode = (
  configurableCalculationNode,
  ticketFields,
  isSpecialist,
  selectedOrder,
  selectedSubscription,
  partner
) => {
  const {
    firstTicketSystemFieldName,
    secondTicketSystemFieldName,
    firstFieldMathematicsOperator,
    singleFirstTextField,
    compareOperator,
    secondFieldMathematicsOperator,
    singleSecondTextField,
  } = configurableCalculationNode;

  const firstFieldValueFromTicket = getTicketFieldBasedOnPartner({
    partner,
    fieldName: firstTicketSystemFieldName,
    ticketFields,
    selectedOrder,
    selectedSubscription,
  });

  const secondFieldValueFromTicket = getTicketFieldBasedOnPartner({
    partner,
    fieldName: secondTicketSystemFieldName,
    ticketFields,
    selectedOrder,
    selectedSubscription,
  });

  if (isUndefined(firstFieldValueFromTicket) || isUndefined(secondFieldValueFromTicket)) {
    let missingFieldValueFromTicketError = 'Unable to process node due to the ticket missing the field';
    if (isSpecialist) {
      missingFieldValueFromTicketError = `${missingFieldValueFromTicketError}\n[Missing Field: ${
        (firstTicketSystemFieldName, secondTicketSystemFieldName)
      }]`;
    }
    throw new ConfigurableNodeError(missingFieldValueFromTicketError);
  }

  const firstFieldValue = calculateResult(
    firstFieldValueFromTicket,
    firstFieldMathematicsOperator,
    singleFirstTextField
  );
  const secondFieldValue = calculateResult(
    secondFieldValueFromTicket,
    secondFieldMathematicsOperator,
    singleSecondTextField
  );

  try {
    return checkCondition({
      fieldValue: firstFieldValue,
      checkType: compareOperator,
      targetValue: secondFieldValue,
    });
  } catch (error) {
    const errorCause = {
      firstFieldValue,
      compareOperator,
      secondFieldValue,
    };
    throw new ConfigurableNodeError(error.message, errorCause);
  }
};
